import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { KTIcon } from "../../../../_metronic/helpers";

interface PhotoUploadProps {
	label: string;
	value?: File | string;
	onChange: (file: File | string | null) => void;
	onUpload?: (file: File) => Promise<any>;
	className?: string;
	isInvalid?: boolean;
	required?: boolean;
	disabled?: boolean;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
	label,
	value,
	onChange,
	onUpload,
	className = "",
	isInvalid = false,
	required = false,
	disabled = false,
}) => {
	const [preview, setPreview] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const getAbsoluteUrl = (path: string) => {
		if (!path) return null;
		const baseUrl =
			import.meta.env.VITE_APP_API_BASE_URL ||
			import.meta.env.VITE_APP_API_URL?.replace(/\/api\/?$/, "") ||
			"";
		const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;

		if (path.startsWith("http")) {
			try {
				const urlObj = new URL(path);
				const baseObj = new URL(baseUrl);
				urlObj.protocol = baseObj.protocol;
				urlObj.host = baseObj.host;
				return urlObj.toString();
			} catch (e) {
				return path;
			}
		}
		const cleanPath = path.replace(/^(\.\/|\/)/, "");
		return `${normalizedBase}${cleanPath}`;
	};

	useEffect(() => {
		if (value instanceof File) {
			const objectUrl = URL.createObjectURL(value);
			setPreview(objectUrl);
			return () => URL.revokeObjectURL(objectUrl);
		} else if (typeof value === "string" && value) {
			setPreview(getAbsoluteUrl(value));
		} else {
			setPreview(null);
		}
	}, [value]);

	const handleFile = async (file: File) => {
		if (file && file.type.startsWith("image/")) {
			const fileSizeMB = file.size / (1024 * 1024);
			if (fileSizeMB > 2.5) {
				toast.error("Profile photo size exceeds 2 MB limit.");
				return;
			}
			if (onUpload) {
				setIsUploading(true);
				try {
					const response = await onUpload(file);
					if (response.data?.status === 1) {
						const uploadedPath =
							response.data?.data?.filename || response.data?.data?.path;
						if (uploadedPath) {
							onChange(uploadedPath);
							toast.success(
								response.data?.message || `${label} uploaded successfully`,
							);
						} else {
							toast.error(
								`Received success status but no file path for ${label}`,
							);
						}
					} else {
						toast.error(response.data?.message || `Failed to upload ${label}`);
					}
				} catch (error: any) {
					console.error(`Failed to upload ${label}`, error);
					const errorMessage =
						error.response?.data?.message || `Failed to upload ${label}`;
					toast.error(errorMessage);
				} finally {
					setIsUploading(false);
				}
			} else {
				onChange(file);
			}
		} else {
			alert("Please upload a valid image file.");
		}
	};

	const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (files && files.length > 0) {
			handleFile(files[0]);
		}
	};

	const triggerUpload = () => {
		if (disabled) return;
		fileInputRef.current?.click();
	};

	const removeImage = (e: React.MouseEvent) => {
		e.stopPropagation();
		onChange(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const viewImage = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (preview) {
			window.open(preview, "_blank");
		}
	};

	const [isBtnHovered, setIsBtnHovered] = useState(false);

	return (
		<div
			className={`d-flex flex-column align-items-center ${className}`}
			style={{ width: "fit-content" }}
		>
			<label
				className={`form-label ${required ? "required" : ""} fw-bold fs-6 mb-3`}
			>
				{label}
			</label>

			<div
				className={`position-relative d-flex align-items-center justify-content-center mb-4 ${isInvalid ? "border-danger" : ""}`}
				style={{
					width: "140px",
					height: "140px",
					border: isInvalid ? "2.5px solid #f1416c" : "2px dashed #d1d5db",
					borderRadius: "12px",
					backgroundColor: isInvalid ? "#fff5f8" : "#f9fafb",
					overflow: "hidden",
					cursor: disabled ? "default" : "pointer",
				}}
				onClick={triggerUpload}
			>
				{preview ? (
					<>
						<img
							src={preview}
							alt="Profile Preview"
							style={{ width: "100%", height: "100%", objectFit: "cover" }}
						/>
						{!disabled && (
							<button
								type="button"
								className="btn btn-icon btn-sm btn-danger position-absolute top-0 end-0 m-1 shadow-sm"
								style={{ width: "24px", height: "24px", borderRadius: "50%" }}
								onClick={removeImage}
							>
								<KTIcon iconName="cross" className="fs-8" />
							</button>
						)}
					</>
				) : (
					<div className="text-center">
						<div className="text-gray-400 fw-bold fs-7">No Image</div>
					</div>
				)}
				{isUploading && (
					<div className="position-absolute inset-0 d-flex align-items-center justify-content-center bg-white bg-opacity-75">
						<span className="spinner-border spinner-border-sm text-primary"></span>
					</div>
				)}
			</div>

			{/* Button and Text Section */}
			<div
				className="d-flex align-items-center w-100 border rounded-3 overflow-hidden mb-2"
				style={{ height: "40px", borderColor: "#cbd5e1" }}
			>
				<button
					type="button"
					className="btn fw-bold fs-7 px-3 h-100 border-end rounded-0 text-nowrap shadow-none"
					onClick={triggerUpload}
					disabled={disabled || isUploading}
					onMouseEnter={() => setIsBtnHovered(true)}
					onMouseLeave={() => setIsBtnHovered(false)}
					style={{
						backgroundColor: isBtnHovered ? "#1b286b" : "#061148",
						color: "#ffffff",
						border: "none",
						transition: "background-color 0.2s ease",
					}}
				>
					{preview ? "Change Photo" : "Choose File"}
				</button>
				<div className="flex-grow-1 px-3 py-1 fs-7 text-gray-700 text-truncate bg-white">
					{preview
						? typeof value === "string"
							? "Photo Selected"
							: (value as File)?.name || "Photo Selected"
						: "No file chosen"}
				</div>
			</div>

			{/* Hint Text */}
			<div
				className="fs-8 text-muted text-center"
				style={{ maxWidth: "200px", lineHeight: "1.4" }}
			>
				Max file size: 2 MB (Allowed file types: JPG, JPEG, PNG)
			</div>

			{preview && !isUploading && (
				<div className="d-flex gap-2 mt-3">
					<button
						type="button"
						className="btn btn-sm btn-primary py-1 px-3 d-flex align-items-center gap-2"
						onClick={viewImage}
						style={{ backgroundColor: "#1e293b", border: "none" }}
					>
						<KTIcon iconName="eye" className="fs-7 text-white" />
						<span className="fs-8">View</span>
					</button>
				</div>
			)}

			<input
				type="file"
				ref={fileInputRef}
				onChange={onFileSelect}
				accept="image/jpeg,image/png,image/jpg"
				style={{ display: "none" }}
			/>
		</div>
	);
};

export { PhotoUpload };
